/**
 * UserDisputeDetail — party-facing dispute detail page (dark theme).
 *
 * Shows the user their dispute status, lets either party submit a statement +
 * evidence, shows both parties' statements/evidence once submitted, and shows
 * what evidence the AI found missing (party-safe subset from
 * POST /ai/dispute-advice — parties never see the recommended
 * decision/confidence while under review).
 *
 * Post-resolution appeals are disabled for controlled launch (a resolution
 * already executes the real payout, so TrustTrade cannot yet offer genuine
 * financial recourse after the fact) — see routes/disputes.py's
 * DISPUTE_APPEALS_ENABLED. That UI is removed here to match; it can come
 * back alongside a real pre-payout appeal hold.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout, { Product as V } from '../components/DashboardLayout';
import PhotoUploader from '../components/PhotoUploader';
import SecureImage from '../components/SecureImage';
import api from '../utils/api';
import { toast } from 'sonner';
import { AlertCircle, ArrowLeft, ShieldQuestion, Lock, Loader2, CheckCircle2, Send } from 'lucide-react';

const STATUS_COLOR = {
  Pending: V.warn,
  'Under Review': V.accent,
  Resolved: V.success,
  Appealed: V.warn,
  Rejected: V.error,
};

function statusColor(status) {
  return STATUS_COLOR[status] || V.sub;
}

function Label({ children }) {
  return (
    <p style={{
      fontSize: 10, color: V.sub, fontFamily: V.sans, textTransform: 'uppercase',
      letterSpacing: '0.1em', marginBottom: 6,
    }}>{children}</p>
  );
}

function Panel({ children, style }) {
  return (
    <div style={{
      background: V.surface, border: `1px solid ${V.border}`, borderRadius: 14,
      padding: '20px 24px', marginBottom: 16, ...style,
    }}>{children}</div>
  );
}

function UserDisputeDetail() {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dispute, setDispute] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [advice, setAdvice] = useState(null);       // party-safe subset
  const [loading, setLoading] = useState(true);
  const [statement, setStatement] = useState('');
  const [evidencePhotos, setEvidencePhotos] = useState([]); // [{filename, preview}]
  const [submittingResponse, setSubmittingResponse] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [disputeId]);

  const fetchData = async () => {
    try {
      const [meRes, disputesRes, txnRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/disputes'),
        api.get('/transactions'),
      ]);
      setUser(meRes.data);

      const found = (disputesRes.data || []).find(d => d.dispute_id === disputeId);
      if (!found) {
        toast.error('Dispute not found');
        navigate('/disputes-dashboard');
        return;
      }
      setDispute(found);
      setTransaction((txnRes.data || []).find(t => t.transaction_id === found.transaction_id) || null);

      // Party-safe AI info (missing evidence + path). Best-effort.
      try {
        const adviceRes = await api.post('/ai/dispute-advice', { dispute_id: disputeId });
        setAdvice(adviceRes.data);
      } catch { /* AI not ready / unavailable — page still renders */ }
    } catch {
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const myRole = (() => {
    if (!transaction || !user) return null;
    const isBuyer = transaction.buyer_user_id === user.user_id ||
      (transaction.buyer_email || '').toLowerCase() === (user.email || '').toLowerCase();
    return isBuyer ? 'buyer' : 'seller';
  })();

  const otherRole = myRole === 'buyer' ? 'seller' : 'buyer';
  const isResolved = (dispute?.status || '').toLowerCase().includes('resolved');
  const responseNeededFromMe = !isResolved && (
    dispute?.needs_response_from === myRole || dispute?.awaiting_response_from === myRole
  );
  const myStatement = myRole === 'buyer' ? dispute?.buyer_statement : dispute?.seller_statement;
  const otherStatement = myRole === 'buyer' ? dispute?.seller_statement : dispute?.buyer_statement;

  const submitResponse = async () => {
    if (!statement.trim() && evidencePhotos.length === 0) {
      toast.error('Add a statement or at least one piece of evidence');
      return;
    }
    setSubmittingResponse(true);
    try {
      await api.post(`/disputes/${disputeId}/respond`, {
        statement: statement.trim() || null,
        evidence_filenames: evidencePhotos.map((p) => p.filename),
      });
      toast.success('Your statement and evidence were submitted.');
      setStatement('');
      setEvidencePhotos([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit your response');
    } finally {
      setSubmittingResponse(false);
    }
  };

  if (loading) {
    return <DashboardLayout user={null} loading darkMain><div /></DashboardLayout>;
  }

  return (
    <DashboardLayout user={user} darkMain>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '8px 0 40px' }}>
        <button
          onClick={() => navigate('/disputes-dashboard')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
            border: 'none', color: V.sub, cursor: 'pointer', fontSize: 13, marginBottom: 16,
            fontFamily: V.sans,
          }}
        >
          <ArrowLeft size={15} /> Back to disputes
        </button>

        <div className="tt-page-header"><span className="tt-eyebrow">Transaction support</span><h1>Dispute details</h1><p style={{ color: V.sub, fontSize: 14 }}>Keep the issue and evidence together. A human administrator reviews the case and records the resolution.</p></div>
        {/* Header / status */}
        <Panel>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Label>Dispute</Label>
              <p style={{ fontFamily: V.mono, fontSize: 14, color: V.text }}>{dispute.dispute_id}</p>
            </div>
            <span style={{
              padding: '4px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, fontFamily: V.mono,
              color: statusColor(dispute.status),
              background: `${statusColor(dispute.status)}18`,
              border: `1px solid ${statusColor(dispute.status)}40`,
            }}>
              {dispute.status}
            </span>
          </div>

          {transaction && (
            <div className="tt-info-grid" style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${V.border}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <Label>Item</Label>
                <p style={{ fontSize: 13, color: V.text }}>{transaction.item_description || '—'}</p>
              </div>
              <div>
                <Label>Amount</Label>
                <p style={{ fontSize: 13, color: V.text, fontFamily: V.mono }}>
                  R {Number(transaction.item_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <Label>Your role</Label>
                <p style={{ fontSize: 13, color: V.text, textTransform: 'capitalize' }}>{myRole || '—'}</p>
              </div>
              <div>
                <Label>Raised</Label>
                <p style={{ fontSize: 13, color: V.text }}>{new Date(dispute.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
        </Panel>

        {/* Resolution (when resolved) */}
        {dispute.resolution ? (
          <Panel style={{ borderColor: `${V.success}55` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: V.success }}>
              <CheckCircle2 size={16} />
              <span style={{ fontSize: 13, fontWeight: 700, fontFamily: V.sans, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Resolution
              </span>
            </div>
            <p style={{ fontSize: 13, color: V.text, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{dispute.resolution}</p>
            {dispute.resolved_at && (
              <p style={{ fontSize: 11, color: V.dim, fontFamily: V.mono, marginTop: 8 }}>
                {new Date(dispute.resolved_at).toLocaleString()}
              </p>
            )}
          </Panel>
        ) : (
          <Panel style={{ borderColor: `${V.error}40`, background: `${V.error}0A` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: V.error }}>
              <Lock size={16} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                Funds frozen — this transaction is under dispute review. No release can happen until it's resolved.
              </span>
            </div>
          </Panel>
        )}

        {/* Party statements + evidence */}
        <Panel>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: V.sans, textTransform: 'uppercase', letterSpacing: '0.08em', color: V.sub }}>
              Statements &amp; Evidence
            </span>
            {responseNeededFromMe && (
              <span style={{
                padding: '3px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700, fontFamily: V.mono,
                color: V.warn, background: `${V.warn}18`, border: `1px solid ${V.warn}40`,
              }}>
                Your response is required
              </span>
            )}
          </div>

          <div className="tt-info-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <Label>Your statement ({myRole || '—'})</Label>
              <p style={{ fontSize: 13, color: myStatement ? V.text : V.dim, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {myStatement || dispute.description || 'No statement submitted yet.'}
              </p>
            </div>
            <div>
              <Label>{otherRole}'s statement</Label>
              <p style={{ fontSize: 13, color: otherStatement ? V.text : V.dim, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {otherStatement || 'No statement submitted yet.'}
              </p>
            </div>
          </div>

          {dispute.evidence_photos?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Label>Evidence on file ({dispute.evidence_photos.length})</Label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 10 }}>
                {dispute.evidence_photos.map((ref, i) => (
                  <SecureImage
                    key={ref + i}
                    reference={ref}
                    category="disputes"
                    alt={`Evidence ${i + 1}`}
                    className="w-full h-24 object-cover rounded"
                    style={{ border: `1px solid ${V.border}` }}
                  />
                ))}
              </div>
            </div>
          )}

          {!isResolved && (
            <div style={{ paddingTop: 16, borderTop: `1px solid ${V.border}` }}>
              <Label>Add your statement / evidence</Label>
              <textarea
                aria-label="Your statement and evidence description"
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                rows={4}
                placeholder="Tell us your side of the story…"
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 14, border: `1px solid ${V.border}`,
                  background: V.hover, color: V.text, fontFamily: V.sans, fontSize: 13,
                  outline: 'none', boxSizing: 'border-box', resize: 'vertical', marginBottom: 12,
                }}
              />
              <div style={{ marginBottom: 12 }}>
                <PhotoUploader
                  photos={evidencePhotos}
                  setPhotos={setEvidencePhotos}
                  minPhotos={0}
                  maxPhotos={5}
                  required={false}
                  evidenceMode
                />
              </div>
              <button
                onClick={submitResponse}
                disabled={submittingResponse}
                style={{
                  padding: '10px 20px', borderRadius: 14, border: 'none',
                  background: submittingResponse ? V.dim : V.accent, color: '#061421',
                  fontFamily: V.sans, fontWeight: 700, fontSize: 13,
                  cursor: submittingResponse ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {submittingResponse ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <><Send size={14} /> Submit</>}
              </button>
            </div>
          )}
        </Panel>

        {/* What evidence is missing (party-safe AI subset) */}
        <Panel style={{ borderColor: `${V.accent}40`, background: `${V.accent}0A` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: V.accent }}>
            <ShieldQuestion size={16} />
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: V.sans, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Strengthen your case
            </span>
          </div>
          {advice?.missing_evidence?.length > 0 ? (
            <>
              <p style={{ fontSize: 12, color: V.sub, marginBottom: 10 }}>
                Evidence suggestions to help prepare your case. A human administrator makes the final decision:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {advice.missing_evidence.map((m, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <AlertCircle size={14} color={V.warn} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: V.text }}>{m}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p style={{ fontSize: 13, color: V.sub }}>
              {advice ? 'No additional evidence was flagged as missing.' : 'Evidence guidance is unavailable. You can still submit your statement and evidence for human review.'}
            </p>
          )}
        </Panel>
        <Panel><h2 style={{ fontSize: 16, color: V.text, margin: '0 0 14px' }}>Case activity</h2><ol className="tt-case-events">{[
          ['Dispute opened', dispute.created_at],
          ['Buyer statement submitted', dispute.buyer_statement_at],
          ['Seller statement submitted', dispute.seller_statement_at],
          ['Resolution recorded', dispute.resolved_at],
        ].filter(([, date]) => date && !Number.isNaN(new Date(date).getTime())).sort((a, b) => new Date(a[1]) - new Date(b[1])).map(([label, date]) => <li key={label}><strong>{label}</strong><time dateTime={date}>{new Date(date).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg' })}</time></li>)}</ol></Panel>
      </div>
    </DashboardLayout>
  );
}

export default UserDisputeDetail;
